import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BOOKING_PAGE_VIEWS, GTM_EVENTS, MEETING_TYPE_ID } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { environment } from '@environments/environment';
import { MeetingTypesModel } from '@models/meeting-types.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { LoggerService } from '@services/utils/logger.service';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { InfoCardComponent } from '@ui-lib/cards/info-card/info-card.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';


@Component({
  selector: 'app-meeting-type-selection',
  templateUrl: './meeting-type-selection.component.html',
  styleUrls: ['./meeting-type-selection.component.scss'],
  imports: [FormsModule, CswTextComponent, InfoCardComponent, ButtonComponent, CalioSafePipe, CalioTranslationPipe, CalioMeetingTemplatePipe, TranslateModule]
})
export class MeetingTypeSelectionComponent implements OnInit {

  readonly imagePath = environment.imageHostName;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly meetingTypeId = MEETING_TYPE_ID;

  selectedMeetingTypeId: number;
  selectedMeetingType: MeetingTypesModel;
  meetingTypes = new Array<MeetingTypesModel>();
  lang: string;
  slotGhostElements = new Array<number>(5);
  showGhostElement = true;
  showMeetingTypeSelectionRequiredError = false;
  isGTMEventSent: boolean;
  protected supportedMeetingTypeIds: number[];

  @Output() gotoPage = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  constructor(
    public translate: TranslateService,
    public widgetService: WidgetService,
    public bookingService: BookingService,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) { }

  ngOnInit(): void {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
    this.isGTMEventSent = false;
    this.bookingService.loadAppointmentStateFromLocalStorage();
    const config = this.widgetService.widgetConf;
    this.supportedMeetingTypeIds = this.bookingService.getSupportedMeetingTypeIdsFromLocalStorage(
      this.bookingService.partnerData.booking_name
    );
    this.getMeetingTypes(config?.context?.meeting_providers);
  }

  getMeetingTypes(ids: number[]): void {
    if (!ids || ids?.length === 0) {
      LoggerService.log('Meeting type proviers are not available');
      // [hidden_booking_steps] No configured provider for a hidden MEETING_TYPE step → un-hide it so
      // the user isn't stranded on an invisible display:none section (never a blank dead-end).
      if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)) {
        this.showGhostElement = false;
        this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
      }
      return;
    }
    this.bookingService.getMeetingTypesByIds(ids).subscribe({
      next: meetingTypes => {
        this.showGhostElement = false;
        if (meetingTypes) {
          // TODO (Chirag): Remove this after calenso meeting type is added to the backend
          this.meetingTypes = meetingTypes.filter(meeting => meeting.id !== 3);
          this.supportedMeetingTypeIds?.length && (this.meetingTypes = this.meetingTypes.filter(meeting => this.supportedMeetingTypeIds.includes(meeting.id)));

          // [hidden_booking_steps] MEETING_TYPE hidden → force the single configured meeting provider
          // and skip in the travel direction (forward on entry, backward when returning through the
          // chain). If no single provider resolves to a fetched meeting type, fall back to rendering
          // the step normally (never a dead-end, never a silent default).
          if (
            this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW) &&
            // If this step already failed to resolve once, it is being rendered normally — treat it
            // as a real reachable step (don't force-skip it again in either direction).
            !this.bookingService.hiddenStepResolutionFailed.has(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)
          ) {
            if (this.bookingService.isNavigatingBackTo(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)) {
              if (this.bookingService.isBeforeQuestionsMode()) {
                this.previous();
                return;
              }
              // First step of the flow: nothing earlier to chain to — render normally and let the
              // existing back-navigation handling below pre-select the single card.
              this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
            } else {
              const context = this.widgetService.widgetConf?.context;
              const providers = context?.meeting_providers ?? [];
              const forcedMeetingType = providers.length === 1
                ? this.meetingTypes.find(meeting => meeting.id === Number(providers[0]))
                : undefined;
              if (forcedMeetingType) {
                // onMeetingTypeChange already advances when one-page / auto-step-forward is on;
                // otherwise advance explicitly. Never do both (would double-navigate).
                this.onMeetingTypeChange(forcedMeetingType.id, false);
                if (!(context?.enable_one_page_ui || context?.auto_step_forward_on_select)) {
                  this.next(false);
                }
              } else {
                this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
              }
              return;
            }
          }

          // Check if user is navigating back from a later step.
          // In that case, don't auto-navigate — let them stay to change their selection.
          const isNavigatingBack = (
            this.bookingService.lastView === BOOKING_PAGE_VIEWS.STORE_VIEW ||
            this.bookingService.lastView === BOOKING_PAGE_VIEWS.SERVICES_VIEW
          );

          if (this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId) {
            this.selectedMeetingType = this.meetingTypes.find(meeting => meeting.id === Number(this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId));
            this.selectedMeetingTypeId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId;
            if (this.widgetService.isDefaultMeetingTypeIdSelected() && !isNavigatingBack) {
              this.onMeetingTypeChange(this.selectedMeetingTypeId, false);
              this.next(false);
            }
          } else {
            if (this.meetingTypes?.length === 1) {
              if (isNavigatingBack) {
                this.selectedMeetingTypeId = this.meetingTypes[0].id;
                this.selectedMeetingType = this.meetingTypes[0];
              } else {
                this.onMeetingTypeChange(this.meetingTypes[0].id, false);
                this.next(false);
              }
            }
          }
        } else {
          LoggerService.warn('No meeting types available');
          this.showGhostElement = false;
          this.meetingTypes = [];
          // [hidden_booking_steps] Null/empty meeting-type result for a hidden MEETING_TYPE step →
          // un-hide it so the user isn't stranded on an invisible section (never a blank dead-end).
          if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)) {
            this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
          }
        }

      },
      error: (error: HttpErrorResponse) => {
        LoggerService.log(error);
        // [hidden_booking_steps] A transport failure fetching the meeting types must NOT leave a
        // hidden MEETING_TYPE step invisibly stuck — un-hide it so the flow recovers.
        if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)) {
          this.showGhostElement = false;
          this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
        }
      }
    });
  }

  /**
   * A partner without the postcode search only gets it forced on by one meeting type (the customer meeting type with
   * show_store_selection_for_customer_meeting_type), so a postcode typed there does not follow the customer to another
   * meeting type: the store step of the new type never rewrites it and the personal form would still lock the zip to it.
   */
  private clearForcedOnStorePostcode(): void {
    const partner = this.widgetService.widgetConf?.partner;
    if (Number(partner?.enable_store_postcode_feature) === 1 || Number(partner?.automatic_store_zip_search) === 1) {
      return;
    }
    const state = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
    state.selectedStoreLeadGeneratorPostcode = null;
    state.storeLeadGeneratorPostcodeEnteredByCustomer = false;
  }

  previous(): void {
    if (this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_meetings_end);
    }
    this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_BEFORE, isUserInteracted: true });
  }

  next(isUserInteracted = true): void {

    // Meeting type is is not selected and clicked on next then show error.
    if (!this.selectedMeetingTypeId) {
      this.showMeetingTypeSelectionRequiredError = true;
      return
    }

    if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId === this.selectedMeetingType.id) {
      this.bookingService.selectedBookingData.selectedMeetingTypes.push(this.selectedMeetingType);
    } else {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState = this.bookingService.resetCurrentSelectedAppointmentStateonStoreSelection();
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = this.selectedMeetingType.id;
      this.clearForcedOnStorePostcode();
      this.bookingService.selectedBookingData.selectedMeetingTypes.push(this.selectedMeetingType);
    }

    if (
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId !== 1 &&
      !this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField &&
      !this.bookingService.selectedBookingData.currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField &&
      !this.widgetService.widgetConf?.context?.filter_meetingtype_services_based_on_store_id
    ) {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = undefined;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = undefined;
    }

    if (isUserInteracted) {
      // trigger start event on first user interaction happen and
      // meeting type selection step is configured to first step and
      // meeting types are more then 1
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_meetings_start);
        this.isGTMEventSent = true;
      }

      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_meetings_end);
    }

    this.bookingService.setAppointmentStateInLocalStorage(
      this.bookingService.selectedBookingData.currentSelectedAppointmentState
    );
    if (this.selectedMeetingTypeId === MEETING_TYPE_ID.STORE) {
      // For Helsana, only_allow_store_selection_for_direct_sales context support is added to for direct sales
      // When preselected store is direct sales and user select on store meeting type, store selection will be appear
      // For all the other cases store selection will be skipped as store is preselected
      if (
        this.widgetService.widgetConf?.context?.only_allow_store_selection_for_direct_sales &&
        !this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStore?.only_accessible_through_deeplink
      ) {
        this.gotoPage.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted });
      } else {
        this.gotoPage.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted });
      }
    } else if (
      this.selectedMeetingTypeId === MEETING_TYPE_ID.CUSTOMER &&
      this.widgetService.widgetConf?.context?.show_store_selection_for_customer_meeting_type
    ) {
      // For Helsana, show_store_selection_for_customer_meeting_type context support is added to show store selection for customer meeting type
      // When user select on customer meeting type, store selection will be appear
      this.gotoPage.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted });
    } else {
      this.gotoPage.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted });
    }
  }

  onMeetingTypeChange(selectedMeetingTypeId: number, isUserInteracted = true): void {
    // Reset Error Flag
    this.showMeetingTypeSelectionRequiredError = false;

    if (isUserInteracted) {
      // trigger start event on first user interaction happen and
      // meeting type selection step is configured to first step and
      // meeting types are more then 1
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_meetings_start);
        this.isGTMEventSent = true;
      }
    }

    this.selectedMeetingTypeId = selectedMeetingTypeId;
    this.selectedMeetingType = this.meetingTypes.find(meeting => meeting.id === this.selectedMeetingTypeId);

    if (
      this.widgetService.widgetConf?.context?.enable_one_page_ui ||
      this.widgetService.widgetConf?.context?.auto_step_forward_on_select
    ) {
      // Proceeds to the next step if the one-page UI is enabled.
      // Or if auto step forward on select is enabled.
      this.next();
    }
  }
}
